import { Inject, Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import type { FirebaseApp } from 'firebase/app';
import type { Messaging } from 'firebase/messaging';
import { environment } from '../../environments/environment';

/** Service worker that renders background pushes — one per origin, shared by every orderYaa portal. */
const SW_URL = '/firebase-messaging-sw.js';
const SW_SCOPE = '/firebase-cloud-messaging-push-scope';

/** Per-surface push wiring. The token is cached per surface so each audience registers independently. */
export interface PushSurface {
  /** Stable id used for the localStorage token cache + the in-flight guard (e.g. 'rider', 'partner'). */
  key: string;
  /** PUT here with { token, platform } to register/refresh this device. */
  registerUrl: string;
  /** DELETE here (with ?token=…) to unregister this device on sign-out. */
  unregisterUrl: string;
}

/**
 * Reusable Firebase Cloud Messaging (web push) client for the RIDER and PARTNER portals — the generic
 * sibling of the customer `PushService`. Registers the device token with the backend so notifications
 * reach this browser and clears it on sign-out. Each surface passes its own {@link PushSurface} (the
 * caller's auth interceptor attaches the right JWT to the register/unregister calls).
 *
 * SSR-safe + best-effort: no-ops on the server, without push support, or without Firebase config. The
 * Firebase SDK is loaded lazily (dynamic import) so it never enters the server bundle or initial chunk.
 *
 * NOTE: the FCM registration token is shared per (browser, Firebase project) across ALL orderYaa
 * portals, so sign-out here does NOT call FCM deleteToken() (that would kill a co-signed-in surface's
 * token). Removing the server-side device row is enough to stop delivery to this audience.
 */
@Injectable({ providedIn: 'root' })
export class DevicePushService {
  private readonly http = inject(HttpClient);
  private readonly isBrowser: boolean;

  private appPromise: Promise<FirebaseApp | null> | null = null;
  private foregroundBound = false;
  private readonly syncing = new Set<string>();

  constructor(@Inject(PLATFORM_ID) platformId: Object) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  /** Call after a FRESH sign-in: request permission (if undecided), then register this device. */
  async register(surface: PushSurface): Promise<void> {
    if (!this.supported || !this.configured) return;
    try {
      let permission = Notification.permission;
      if (permission === 'default') permission = await Notification.requestPermission();
      if (permission !== 'granted') return;
      // Fresh sign-in: drop any cached token so syncToken always re-PUTs — a device that switched
      // users (no logout in between) must re-point its server row to the new recipient.
      this.clearLocal(surface);
      await this.syncToken(surface);
    } catch {
      /* best-effort — never break sign-in over push */
    }
  }

  /** Call on app startup for a returning signed-in user: refresh the token only if already granted. */
  async syncIfPermitted(surface: PushSurface): Promise<void> {
    if (!this.supported || !this.configured) return;
    if (Notification.permission !== 'granted') return;
    try {
      await this.syncToken(surface);
    } catch {
      /* best-effort */
    }
  }

  /** Call on sign-out — remove this device's server row (while the JWT is still attached) + clear cache. */
  async unregister(surface: PushSurface): Promise<void> {
    if (!this.isBrowser) return;
    const token = localStorage.getItem(this.cacheKey(surface));
    this.clearLocal(surface);
    if (!token) return;
    try {
      await firstValueFrom(this.http.delete(`${surface.unregisterUrl}?token=${encodeURIComponent(token)}`));
    } catch {
      /* ignore */
    }
  }

  /** Forget the cached token locally (e.g. dead-session teardown) so the next sign-in re-registers. */
  clearLocal(surface: PushSurface): void {
    if (this.isBrowser) localStorage.removeItem(this.cacheKey(surface));
  }

  // ── internals ──────────────────────────────────────────────────────────────

  private cacheKey(surface: PushSurface): string {
    return `orderyaa.push.${surface.key}`;
  }

  private get configured(): boolean {
    const c = environment.firebase;
    return !!(c && c.apiKey && c.projectId && c.appId && c.messagingSenderId && environment.firebaseVapidKey);
  }

  private get supported(): boolean {
    return this.isBrowser
      && typeof Notification !== 'undefined'
      && typeof PushManager !== 'undefined'
      && 'serviceWorker' in navigator;
  }

  private async syncToken(surface: PushSurface): Promise<void> {
    if (this.syncing.has(surface.key)) return;
    this.syncing.add(surface.key);
    try {
      const { getMessaging, getToken, isSupported } = await import('firebase/messaging');
      if (!(await isSupported())) return;

      const app = await this.ensureApp();
      if (!app) return;

      const registration = await navigator.serviceWorker.register(SW_URL, { scope: SW_SCOPE });
      const messaging = getMessaging(app);
      const token = await getToken(messaging, {
        vapidKey: environment.firebaseVapidKey,
        serviceWorkerRegistration: registration,
      });
      if (!token) return;

      this.bindForeground(messaging);

      if (localStorage.getItem(this.cacheKey(surface)) === token) return; // unchanged — nothing to send
      await firstValueFrom(this.http.put(surface.registerUrl, { token, platform: 'web' }));
      localStorage.setItem(this.cacheKey(surface), token);
    } finally {
      this.syncing.delete(surface.key);
    }
  }

  private ensureApp(): Promise<FirebaseApp | null> {
    this.appPromise ??= (async () => {
      const { initializeApp, getApps, getApp } = await import('firebase/app');
      return getApps().length ? getApp() : initializeApp(environment.firebase);
    })().catch(() => null);
    return this.appPromise;
  }

  /** Foreground messages don't auto-display — surface them as a native notification once. */
  private async bindForeground(messaging: Messaging): Promise<void> {
    if (this.foregroundBound) return;
    this.foregroundBound = true;
    const { onMessage } = await import('firebase/messaging');
    onMessage(messaging, (payload) => {
      const n = payload.notification;
      if (!n?.title || Notification.permission !== 'granted') return;
      try {
        new Notification(n.title, { body: n.body ?? '', icon: '/images/logo.png' });
      } catch {
        // Some (mostly mobile) browsers forbid the Notification constructor — background SW still delivers.
      }
    });
  }
}
